// The page's small subscriptions to things outside React — a clock, an
// object URL and the hash. The first two are effects because each
// synchronises with an external system and cleans up after itself
// (react-useeffect-discipline §3.1); nothing here starts a recording or
// posts one, which happen in event handlers.
import { useEffect, useState, useSyncExternalStore } from "react";

// `performance.now()` — the capture driver's clock — refreshed while
// `active`, for a countdown. The capture machine ticks internally but only
// re-renders when the warn cue flips, so the readout keeps its own clock.
export function useNow(active: boolean, intervalMs = 100): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!active) return;
    // oxlint-disable-next-line react/set-state-in-effect -- restart the clock from now when the countdown turns on, not from the last tick
    setNow(performance.now());
    const id = setInterval(() => setNow(performance.now()), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);
  return now;
}

// An object URL for playback, revoked when the blob changes or the panel
// unmounts (packages/speech/src/capture/README.md: the screen that plays a
// recording back owns its URL). Created in the effect rather than during
// render, so StrictMode's mount–unmount–mount cannot hand <audio> a URL its
// own cleanup already revoked.
export function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (blob === null) {
      // oxlint-disable-next-line react/set-state-in-effect -- the URL is created and revoked in the effect; see the comment above
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob]);
  return url;
}

export type View = "bench" | "compose";

const subscribeHash = (callback: () => void) => {
  window.addEventListener("hashchange", callback);
  return () => window.removeEventListener("hashchange", callback);
};
const readView = (): View =>
  window.location.hash === "#compose" ? "compose" : "bench";

// Which page the hash names; a store subscription rather than an effect
// (react-useeffect-discipline §3.3).
export const useHashView = (): View =>
  useSyncExternalStore(subscribeHash, readView, () => "bench");
